import {
  Badge, Button, Card, ChildAvatar, ErrorState, Field, GALLERY_ACCEPT, Input, LARGEST_PAGE, Notice, Row, Select, Stack,
  isVideoFile, measureVideoDuration, noSave, noSaveVideo, sizeProblem, useApi, useI18n, useLimits,
  type PhotoEdit, type PhotoVisibilityMode,
} from '@daghfal/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoEditor } from '../components/PhotoEditor';

/** The name an edited photograph is sent under. The API types a file by its first bytes, never by its name. */
const EDITED_NAME = 'photo.jpg';

/**
 * Capture, tag and publish a photograph or a video, in one action (FR-STF-08).
 *
 * A teacher photographing an activity wants one screen, not three. The camera opens from the file input's
 * `capture` attribute on a phone and falls back to the picker everywhere else.
 *
 * This is the one thing that does not go through the offline queue, and deliberately: a `File` does not
 * survive `localStorage`, so a queued photograph would be a promise the app could not keep. The upload
 * happens here, while the bytes are still in hand, and fails visibly if there is no connection.
 *
 * Consent is shown before it is refused (Q45): a child without individual-photos consent cannot be uploaded at
 * all, and in shared mode a classmate without group-photos consent is greyed out rather than tagged and then
 * named in a refusal. The server still checks both; this only saves the teacher the round trip.
 *
 * A photograph can be cropped and blurred first (Q1), so that a group photo shows only the child it is for.
 * Once edited, only the edited photograph is sent; the one taken stays on the phone. A video cannot be edited.
 */
export function PhotoUpload() {
  const { childId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { t, formatMegabytes } = useI18n();
  const { media } = useLimits();

  const id = Number(childId);

  // The file as chosen, the edits made to it, and the photograph drawn from them. What is sent is the edited
  // photograph where there is one: never the original once anything has been blurred.
  const [chosen, setChosen] = useState<File | null>(null);
  const [edits, setEdits] = useState<PhotoEdit[]>([]);
  const [edited, setEdited] = useState<File | null>(null);
  const [editing, setEditing] = useState(false);
  const file = edited ?? chosen;

  const [tooLarge, setTooLarge] = useState<'photo' | 'video' | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [occasionId, setOccasionId] = useState<number | ''>('');
  const [visibility, setVisibility] = useState<PhotoVisibilityMode>('SingleChild');
  const [alsoTagged, setAlsoTagged] = useState<number[]>([]);

  const child = useQuery({
    queryKey: ['child', id],
    queryFn: () => api.children.get(id),
    enabled: Number.isFinite(id),
  });

  const classmates = useQuery({
    queryKey: ['children', { classId: child.data?.classId }],
    queryFn: () => api.children.list({ classId: child.data!.classId!, status: 'Active', pageSize: LARGEST_PAGE }),
    enabled: child.data?.classId != null,
  });

  const occasions = useQuery({ queryKey: ['occasions'], queryFn: () => api.photos.occasions() });

  const upload = useMutation({
    mutationFn: async () => api.photos.upload(file!, {
      childIds: [id, ...alsoTagged],
      caption: caption.trim() || null,
      occasionId: occasionId === '' ? null : occasionId,
      visibilityMode: visibility,
      takenAt: null,
      // One action, reaching the family at the end of it. A setting that reviews first turns this off in
      // its settings and publishes from the console instead.
      publishImmediately: true,
      durationMs: isVideoFile(file!) ? await measureVideoDuration(file!) : null,
    }),
    onSuccess: () => navigate(`/child/${id}`),
  });

  // Refused here, before the upload starts: the server would cut an oversized body off mid-upload, which
  // the browser reports as a lost connection rather than as a reason.
  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] ?? null;
    const problem = picked ? sizeProblem(picked, media) : null;
    setTooLarge(problem);
    setChosen(problem ? null : picked);
    setEdits([]);
    setEdited(null);
  };

  // An edited photograph over the limit stays the one in hand, with the upload refused: falling back to the
  // original would send the faces the teacher has just blurred.
  const applyEdit = (next: PhotoEdit[], drawn: Blob | null) => {
    const result = drawn ? new File([drawn], EDITED_NAME, { type: drawn.type }) : null;
    setEdits(next);
    setEdited(result);
    setTooLarge(result ? sizeProblem(result, media) : null);
    setEditing(false);
  };

  // One object URL per file in hand, revoked when it is replaced or the screen closes. Minting it inline
  // in the markup would hand the browser a fresh URL — and another copy of a multi-megabyte photograph —
  // on every keystroke in the caption field, with nothing ever releasing them.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Single-child means exactly one child in the frame, so the extra tags only appear in shared mode.
  const others = (classmates.data?.items ?? []).filter((candidate) => candidate.id !== id);
  const photographable = child.data?.individualPhotoConsent ?? true;
  const groupConsent = child.data?.groupPhotoConsent ?? true;
  const video = file != null && isVideoFile(file);
  const someoneGreyedOut = others.some((candidate) => !candidate.individualPhotoConsent || !candidate.groupPhotoConsent);

  // The form stays mounted under the editor, only hidden: unmounting it would empty the file field, and the
  // teacher would come back from the editor to "no file chosen" with a photograph still in hand.
  return (
    <Stack>
      {editing && chosen && (
        <PhotoEditor file={chosen} edits={edits} onApply={applyEdit} onCancel={() => setEditing(false)} />
      )}

      <div hidden={editing}>
        <Stack>
          <Button variant="ghost" onClick={() => navigate(`/child/${id}`)}>{t('action.back')}</Button>

          <Card title={t('photos.upload')} hint={child.data?.preferredName ?? child.data?.firstName}>
            <Stack>
              {upload.isError && <ErrorState error={upload.error} />}
              {!photographable && <Notice tone="warning">{t('photos.childNotPhotographable')}</Notice>}

              <Field label={t('photos.upload')} required>
                {(props) => (
                  <Input
                    {...props}
                    type="file"
                    accept={GALLERY_ACCEPT}
                    capture="environment"
                    onChange={choose}
                  />
                )}
              </Field>
              <p className="dg-muted dg-text-sm">{t('photos.video.hint', { mb: formatMegabytes(media.videoMaximumBytes) })}</p>
              {tooLarge && (
                <Notice tone="warning">
                  {tooLarge === 'video'
                    ? t('photos.tooLarge.video', { mb: formatMegabytes(media.videoMaximumBytes) })
                    : t('photos.tooLarge.photo', { mb: formatMegabytes(media.photoMaximumBytes) })}
                </Notice>
              )}

              {preview && file && (
                <figure className="dg-photo dg-photo--whole">
                  {video
                    ? <video {...noSaveVideo} src={preview} controls playsInline preload="metadata" aria-label={t('photos.video')} />
                    : <img {...noSave} src={preview} alt="" />}
                </figure>
              )}

              {file && (video
                ? <p className="dg-muted dg-text-sm">{t('editor.hint.video')}</p>
                : (
                  <>
                    {edited
                      ? <Notice tone="success">{t('editor.applied')}</Notice>
                      : visibility === 'SingleChild' && <p className="dg-muted dg-text-sm">{t('editor.hint.single')}</p>}
                    <Row>
                      <Button variant="secondary" disabled={!photographable} onClick={() => setEditing(true)}>
                        {t('editor.open')}
                      </Button>
                    </Row>
                  </>
                ))}

              <Field label={t('photos.caption')}>
                {(props) => (
                  <Input {...props} value={caption} onChange={(event) => setCaption(event.target.value)} />
                )}
              </Field>

              <Field label={t('photos.occasion')}>
                {(props) => (
                  <Select
                    {...props}
                    value={occasionId}
                    onChange={(event) =>
                      setOccasionId(event.target.value === '' ? '' : Number(event.target.value))}
                  >
                    <option value="">—</option>
                    {(occasions.data ?? []).map((occasion) => (
                      <option key={occasion.id} value={occasion.id}>{occasion.name}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('photos.visibility')} required>
                {(props) => (
                  <Select
                    {...props}
                    value={visibility}
                    onChange={(event) => {
                      const next = event.target.value as PhotoVisibilityMode;
                      setVisibility(next);
                      if (next === 'SingleChild') setAlsoTagged([]);
                    }}
                  >
                    <option value="SingleChild">{t('photos.visibility.SingleChild')}</option>
                    <option value="SharedTagged" disabled={!groupConsent}>{t('photos.visibility.SharedTagged')}</option>
                  </Select>
                )}
              </Field>

              {visibility === 'SharedTagged' && (
                <>
                  {/* The server refuses publication where any tagged child lacks a current group-photos consent,
                      and names them, so the teacher knows whose face to blur (FR-PHO-06, Q45, Q1). */}
                  <Notice tone="warning">{t('photos.visibility.SharedTagged')}</Notice>

                  <fieldset className="dg-field">
                    <legend className="dg-field__label">{t('photos.tagged')}</legend>
                    <Stack tight>
                      {others.map((candidate) => {
                        const mayTag = candidate.individualPhotoConsent && candidate.groupPhotoConsent;
                        return (
                          <label key={candidate.id} className="dg-checkbox" aria-disabled={!mayTag}>
                            <input
                              type="checkbox"
                              disabled={!mayTag}
                              checked={alsoTagged.includes(candidate.id)}
                              onChange={(event) => setAlsoTagged((current) => event.target.checked
                                ? [...current, candidate.id]
                                : current.filter((value) => value !== candidate.id))}
                            />
                            {/* The face, so the teacher tags the child in the picture; the allergy on it (Q75, Q76). */}
                            <ChildAvatar child={candidate} photoUrl={candidate.photoUrl} size="sm" allergies={candidate.allergyMarks} />
                            <span>
                              {candidate.preferredName ?? candidate.firstName} {candidate.lastName}
                              {!candidate.individualPhotoConsent && <Badge tone="neutral">{t('photos.noConsent')}</Badge>}
                              {candidate.individualPhotoConsent && !candidate.groupPhotoConsent && (
                                <Badge tone="neutral">{t('photos.noGroupConsent')}</Badge>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </Stack>
                  </fieldset>
                  {someoneGreyedOut && !video && <p className="dg-muted dg-text-sm">{t('editor.hint.shared')}</p>}
                </>
              )}

              <Row>
                <Button
                  busy={upload.isPending}
                  busyLabel={t('action.saving')}
                  disabled={!file || tooLarge !== null || !photographable}
                  onClick={() => upload.mutate()}
                >
                  {t('photos.upload')}
                </Button>
              </Row>
            </Stack>
          </Card>
        </Stack>
      </div>
    </Stack>
  );
}
